// 約定結果の集計 (KPI カード / エリア × 売買 別 集計) — spot / intraday 共通。
//
// spec:
//   - `docs/specs/bidding/spot-bidding.md` AC-BSP-002a (KPI 4 枚) / AC-BSP-002b (集計 7 列)
//   - `docs/specs/bidding/intraday-bidding.md` AC-BIN-022b (同一列構成の共通コンポーネント)
//   - 収支は出さない (spot-bidding.md AC-BSP-090 / intraday-bidding.md AC-BIN-079)。
//     手数料が固定か従量かは事業者の契約によって違い、 単価 1 本の式では正しい値にならないため
// design: `design/js/exec-summary.js` (= プロト側の共通コンポーネント、 列構成・行ルールの SoT)
//
// 単位は **コマ別 MW** (= AC-BSP-002a / 002b が MW 建てで定義している)。 保持・書込 contract も
// MW で統一済みなので (issue #4285、 SoT: `~/models/komaQuantity`)、 呼び出し側の写像に
// MW↔MWh 換算は挟まない。
//
// TODO(business-logic): 集計は本来 backend / BFF が返すべき値。 現状 spot 約定結果の集計 RPC が
// 無く、 行データは画面に全件揃っているため Phase 1 は client 集計とする (= 呼び出し元の
// 明示判断)。 BFF read 集約 (#3851 の bff 項目) 実装時に本 model は
// 「サーバー値の表示」 へ置き換える。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'

import { JEPX_AREA_CODES } from '~/models/area'

/** 集計の入力 1 件 (= 札 1 枚を市場非依存の形に正規化したもの)。 */
export type ExecutionSummaryRecord = {
  areaCode: AreaCode
  side: Side
  /** 入札量 (MW)。 */
  bidVolumeMw: number
  /** 約定量 (MW)。 未約定は 0。 */
  execVolumeMw: number
  /** 約定価格 (円/kWh)。 未約定は undefined。 */
  execPriceYenPerKwh: number | undefined
  /**
   * 取消・失効の札か。 true の札は「入札件数」 には数えるが、 約定率の分母・分子
   * (= 入札MW / 約定MW) からは除外する (AC-BSP-002a / 002b)。
   *
   * TODO(Phase 2): boolean 一値なので「約定はしたが残量を板取消した」 を表現できない
   * (= 約定した量そのものが約定MW・約定総量・約定率・加重平均のすべてから抜ける)。 スポットは
   * 一斉約定で 1 入札 = 0 or 1 約定行になるため発生しないが (`mock-app/docs/domain-knowledge.md`)、
   * 時間前 (AC-BIN-022b) と
   * 共用する時に「除外理由」 を持つ型へ広げる必要がある。
   */
  excluded: boolean
  /**
   * 合計行の「入札件数」 で 1 件にまとめる単位。 同じ値の record は合計行で 1 件と数える。
   * 省略した record はそれぞれ 1 件。
   *
   * エリアで分けない件数は LINK / LOOP の 1 組を 1 件と数える (AC-BSP-002b) ため、 ブロック
   * 入札はグループの id を入れる (エリア × 売買の行では Leg ごとに 1 件のまま)。
   */
  groupKey?: string
  /**
   * 約定量のうち約定価格が判っている分 (MW)。 加重平均約定価格の重みに使う。 省略時は
   * 約定価格があれば約定量の全量。
   *
   * 複数のコマを 1 record にまとめた札 (ブロック入札の Leg) で、 一部のコマだけ約定価格が
   * 未取得の時に、 その分の約定量を加重平均の重みに入れないために渡す。
   */
  pricedExecVolumeMw?: number
}

/** エリア × 売買 別 集計の 1 行 (= spec の 7 列)。 */
export type ExecutionAreaSummaryRow = {
  areaCode: AreaCode
  side: Side
  /** 入札件数 (= 取消・失効も含む)。 */
  bidCount: number
  /** 入札量 (MW) 合計 (= 取消・失効を除く)。 */
  bidVolumeMw: number
  /** 約定量 (MW) 合計 (= 取消・失効を除く)。 */
  execVolumeMw: number
  /** 約定率 (%)。 入札 MW が 0 (= 全札が取消・失効) なら undefined (= 「—」)。 */
  fillRatePct: number | undefined
  /** 加重平均約定価格 (円/kWh)。 未約定 (約定量 0) なら undefined (= 「—」)。 */
  weightedAvgPriceYenPerKwh: number | undefined
}

/** 合計行 (= 全 (エリア × 売買) 行の合算)。 */
export type ExecutionSummaryTotal = Omit<
  ExecutionAreaSummaryRow,
  'areaCode' | 'side'
>

/** KPI カード 4 枚 (AC-BSP-002a) の値。 */
export type ExecutionKpi = {
  /** 約定総量 (買) MW。 */
  buyExecVolumeMw: number
  /** 約定総量 (売) MW。 */
  sellExecVolumeMw: number
  /** 加重平均約定価格 (円/kWh、 買・売 合算)。 約定なしは undefined。 */
  weightedAvgPriceYenPerKwh: number | undefined
  /** 約定率 (%)。 入札 MW が 0 なら undefined。 */
  fillRatePct: number | undefined
}

/** 集計途中の accumulator (= 行 / 合計 / KPI で共有する)。 */
type Bucket = {
  bidCount: number
  bidVolumeMw: number
  execVolumeMw: number
  /** Σ(約定価格 × 約定量)。 加重平均の分子。 */
  priceVolumeSum: number
  /**
   * 約定価格が判っている分だけの約定量。 加重平均の分母。
   *
   * execVolumeMw をそのまま分母にすると、 「約定量はあるが価格が未取得」 の札の
   * 量が分子 (priceVolumeSum) に入らないまま分母だけ増え、 加重平均が下振れする。
   * 約定率の分子は「約定した量」 なので execVolumeMw のままでよく、 加重平均だけ
   * 分母を分けるのが正しい。
   */
  pricedExecVolumeMw: number
}

const emptyBucket = (): Bucket => ({
  bidCount: 0,
  bidVolumeMw: 0,
  execVolumeMw: 0,
  priceVolumeSum: 0,
  pricedExecVolumeMw: 0,
})

const accumulate = (bucket: Bucket, record: ExecutionSummaryRecord): void => {
  bucket.bidCount += 1
  // 取消・失効は「入札した実績」 としては数えるが、 約定率の母集合からは外す (spec 明記)。
  if (record.excluded) return

  bucket.bidVolumeMw += record.bidVolumeMw
  bucket.execVolumeMw += record.execVolumeMw
  // 約定量はあるが価格が未取得の札 (eSLJ 連携では約定量だけ先に入ることがある) は
  // 加重平均の母集合に入れない。
  const pricedVolume = record.pricedExecVolumeMw ?? record.execVolumeMw
  if (record.execPriceYenPerKwh === undefined || pricedVolume <= 0) return
  // 加重平均は符号を使わないので売買方向 UNSPECIFIED でも母集合に入れる。
  bucket.priceVolumeSum += record.execPriceYenPerKwh * pricedVolume
  bucket.pricedExecVolumeMw += pricedVolume
}

const fillRatePctOf = (bucket: Bucket): number | undefined =>
  bucket.bidVolumeMw > 0
    ? (bucket.execVolumeMw / bucket.bidVolumeMw) * 100
    : undefined

const weightedAvgPriceOf = (bucket: Bucket): number | undefined =>
  bucket.pricedExecVolumeMw > 0
    ? bucket.priceVolumeSum / bucket.pricedExecVolumeMw
    : undefined

const bucketKey = (areaCode: AreaCode, side: Side): string =>
  `${areaCode}|${side}`

/**
 * (エリア × 売買) ごとの集計行 + 合計行を返す (AC-BSP-002b)。
 *
 * - 行は 入札が 1 件以上ある組合せのみ。 並びは エリア (北→南) → 買 → 売
 * - 未約定 (約定量 0) の行は 加重平均約定価格 を undefined (= 「—」 表示)
 */
export const computeExecutionAreaSummary = (
  records: ExecutionSummaryRecord[],
): { rows: ExecutionAreaSummaryRow[]; total: ExecutionSummaryTotal } => {
  const buckets = new Map<string, Bucket>()
  const total = emptyBucket()

  const totalGroups = new Set<string>()
  let ungroupedCount = 0
  for (const record of records) {
    const key = bucketKey(record.areaCode, record.side)
    const bucket = buckets.get(key) ?? emptyBucket()
    accumulate(bucket, record)
    buckets.set(key, bucket)
    accumulate(total, record)
    if (record.groupKey === undefined) ungroupedCount += 1
    else totalGroups.add(record.groupKey)
  }
  total.bidCount = ungroupedCount + totalGroups.size

  // 並び順: JEPX_AREA_CODES (北→南) → 買 → 売。 JEPX 対象外エリア (= 沖縄や想定外値) の
  // 札もデータとしては存在し得るため、 定義順の後ろに落とさず末尾へ追加して欠落させない。
  const orderedKeys: string[] = []
  for (const areaCode of JEPX_AREA_CODES) {
    for (const side of [Side.BUY, Side.SELL]) {
      const key = bucketKey(areaCode, side)
      if (buckets.has(key)) orderedKeys.push(key)
    }
  }
  for (const key of buckets.keys()) {
    if (!orderedKeys.includes(key)) orderedKeys.push(key)
  }

  const rows = orderedKeys.map((key): ExecutionAreaSummaryRow => {
    const [areaCode, side] = key.split('|').map(Number)
    const bucket = buckets.get(key) ?? emptyBucket()
    return {
      areaCode,
      side,
      bidCount: bucket.bidCount,
      bidVolumeMw: bucket.bidVolumeMw,
      execVolumeMw: bucket.execVolumeMw,
      fillRatePct: fillRatePctOf(bucket),
      weightedAvgPriceYenPerKwh: weightedAvgPriceOf(bucket),
    }
  })

  return {
    rows,
    total: {
      bidCount: total.bidCount,
      bidVolumeMw: total.bidVolumeMw,
      execVolumeMw: total.execVolumeMw,
      fillRatePct: fillRatePctOf(total),
      weightedAvgPriceYenPerKwh: weightedAvgPriceOf(total),
    },
  }
}

/** KPI カード 4 枚の値を返す (AC-BSP-002a)。 */
export const computeExecutionKpi = (
  records: ExecutionSummaryRecord[],
): ExecutionKpi => {
  const total = emptyBucket()
  let buyExecVolumeMw = 0
  let sellExecVolumeMw = 0

  for (const record of records) {
    accumulate(total, record)
    if (record.excluded) continue
    if (record.side === Side.BUY) buyExecVolumeMw += record.execVolumeMw
    else if (record.side === Side.SELL) sellExecVolumeMw += record.execVolumeMw
  }

  return {
    buyExecVolumeMw,
    sellExecVolumeMw,
    weightedAvgPriceYenPerKwh: weightedAvgPriceOf(total),
    fillRatePct: fillRatePctOf(total),
  }
}
